import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  SmartToy,
  AutoAwesome,
  AutoFixHigh,
  Business,
  AttachMoney,
  Schedule,
  CheckCircle
} from '@mui/icons-material';



const aiSuggestions = [
  {
    type: 'bundling',
    title: 'Smart Bundle Opportunity',
    description: 'Combine SKU-1234 and SKU-5678 to meet MOQ and save $2,400 on shipping',
    savings: 2400,
    confidence: 94
  },
  {
    type: 'timing',
    title: 'Optimal Order Timing',
    description: 'Order in next 7 days to avoid 15% price increase effective March 1st',
    urgency: 'high',
    confidence: 89
  },
  {
    type: 'supplier',
    title: 'Alternative Supplier',
    description: 'Supplier-B offers 8% better pricing for bulk orders over 500 units',
    savings: 3200,
    confidence: 87
  }
];

const steps = [
  'AI Analysis & Recommendations',
  'Supplier Selection & Bundling',
  'Pricing & Terms Optimization',
  'Approval Workflow & Routing',
  'Review & Confirmation'
];

interface POItem {
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  supplier: string;
  leadTime: number;
  moq: number;
  selected: boolean;
}

interface SmartPO {
  id: string;
  items: POItem[];
  totalValue: number;
  approvalLevel: string;
  estimatedSavings: number;
  aiConfidence: number;
  status: 'draft' | 'pending_approval' | 'approved' | 'sent';
}

export const SmartPurchaseOrderWizard: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState<POItem[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<SmartPO[]>([]);
  const [aiEnabled, setAiEnabled] = useState(true);

  const [showAiDialog, setShowAiDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mock PO items
  const mockItems: POItem[] = useMemo(() => [
    {
      sku: 'CHI-IRON-001',
      name: 'CHI Ceramic Flat Iron',
      quantity: 100,
      unitPrice: 65.00,
      totalPrice: 6500,
      supplier: 'Premium Beauty Supplies',
      leadTime: 14,
      moq: 50,
      selected: true
    },
    {
      sku: 'DEV-40V-001',
      name: 'Developer 40 Volume',
      quantity: 200,
      unitPrice: 12.50,
      totalPrice: 2500,
      supplier: 'Hair Products Inc',
      leadTime: 7,
      moq: 100,
      selected: true
    },
    {
      sku: 'COLOR-KIT-001',
      name: 'Professional Color Kit',
      quantity: 75,
      unitPrice: 45.00,
      totalPrice: 3375,
      supplier: 'Beauty Supply Co',
      leadTime: 21,
      moq: 25,
      selected: false
    }
  ], []);

  useEffect(() => {
    setSelectedItems(mockItems);
  }, [mockItems]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      // Final step - create POs
      createPurchaseOrders();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedItems(mockItems);
    setPurchaseOrders([]);
  };

  const handleItemSelection = (sku: string, selected: boolean) => {
    setSelectedItems(prev => prev.map(item => 
      item.sku === sku ? { ...item, selected } : item
    ));
  };

  const handleQuantityChange = (sku: string, quantity: number) => {
    setSelectedItems(prev => prev.map(item => 
      item.sku === sku ? { ...item, quantity, totalPrice: item.unitPrice * quantity } : item
    ));
  };

  const createPurchaseOrders = () => {
    const selectedItemsList = selectedItems.filter(item => item.selected);
    
    // Group by supplier
    const supplierGroups = selectedItemsList.reduce((groups, item) => {
      if (!groups[item.supplier]) {
        groups[item.supplier] = [];
      }
      groups[item.supplier].push(item);
      return groups;
    }, {} as Record<string, POItem[]>);

    const orders: SmartPO[] = Object.entries(supplierGroups).map(([supplier, items]) => {
      const totalValue = items.reduce((sum, item) => sum + item.totalPrice, 0);
      const approvalLevel = totalValue < 1000 ? 'Auto-approve' :
                           totalValue < 10000 ? 'Manager' :
                           totalValue < 50000 ? 'Director' : 'CEO';
      
      return {
        id: `PO-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        items,
        totalValue,
        approvalLevel,
        estimatedSavings: Math.round(totalValue * 0.08), // 8% estimated savings
        aiConfidence: Math.round(Math.random() * 20 + 80), // 80-100% confidence
        status: 'draft'
      };
    });

    setPurchaseOrders(orders);
  };

  const simulateAiAnalysis = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowAiDialog(true);
    }, 2000);
  };

  const getTotalValue = () => {
    return selectedItems
      .filter(item => item.selected)
      .reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const getApprovalLevel = (value: number) => {
    if (value < 1000) return { level: 'Auto-approve', color: 'success' as const };
    if (value < 10000) return { level: 'Manager', color: 'info' as const };
    if (value < 50000) return { level: 'Director', color: 'warning' as const };
    return { level: 'CEO', color: 'error' as const };
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Smart Purchase Order Wizard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={aiEnabled}
                onChange={(e) => setAiEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="AI Assistant"
          />
          <Chip
            icon={<SmartToy />}
            label="AI Powered"
            color={aiEnabled ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* AI Status Banner */}
      {aiEnabled && (
        <Alert 
          severity="info" 
          icon={<AutoAwesome />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={simulateAiAnalysis}>
              Run AI Analysis
            </Button>
          }
        >
          <strong>AI Assistant Active:</strong> Analyzing inventory patterns, supplier performance, 
          and market conditions to optimize your purchase orders.
        </Alert>
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 3 }}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ mt: 4 }}>
        {/* Step 1: AI Analysis & Recommendations */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              AI Analysis & Recommendations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Our AI has analyzed your inventory patterns, supplier performance, and market conditions.
            </Typography>

            {/* AI Suggestions */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {aiSuggestions.map((suggestion, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ 
                    border: '2px solid',
                    borderColor: suggestion.type === 'bundling' ? 'success.main' :
                                 suggestion.type === 'timing' ? 'warning.main' : 'info.main'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {suggestion.type === 'bundling' && <AutoFixHigh color="success" />}
                        {suggestion.type === 'timing' && <Schedule color="warning" />}
                        {suggestion.type === 'supplier' && <Business color="info" />}
                        <Typography variant="subtitle2" color="primary">
                          {suggestion.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" gutterBottom>
                        {suggestion.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Chip 
                          label={`${suggestion.confidence}% confidence`}
                          color="primary"
                          size="small"
                        />
                        {suggestion.savings && (
                          <Typography variant="h6" color="success.main">
                            ${suggestion.savings.toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* AI Confidence Meter */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Confidence Level
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={89} 
                      sx={{ height: 10, borderRadius: 5 }}
                      color="success"
                    />
                  </Box>
                  <Typography variant="h6" color="success.main">
                    89%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  High confidence based on historical data, supplier performance, and market analysis
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Step 2: Supplier Selection & Bundling */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Supplier Selection & Intelligent Bundling
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select items and let AI optimize bundling for maximum savings and efficiency.
            </Typography>

            <Card>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.length > 0 && selectedItems.every(item => item.selected)}
                            indeterminate={selectedItems.some(item => item.selected) && !selectedItems.every(item => item.selected)}
                            onChange={(e) => setSelectedItems(prev => prev.map(item => ({ ...item, selected: e.target.checked })))}
                          />
                        </TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Lead Time</TableCell>
                        <TableCell align="right">MOQ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedItems.map((item) => (
                        <TableRow key={item.sku} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={item.selected}
                              onChange={(e) => handleItemSelection(item.sku, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{item.sku}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{item.supplier}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleQuantityChange(item.sku, Number(e.target.value))}
                              size="small"
                              sx={{ width: 80 }}
                              inputProps={{ min: item.moq }}
                            />
                          </TableCell>
                          <TableCell align="right">${item.unitPrice.toFixed(2)}</TableCell>
                          <TableCell align="right">${item.totalPrice.toFixed(2)}</TableCell>
                          <TableCell align="right">{item.leadTime} days</TableCell>
                          <TableCell align="right">{item.moq}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="primary">
                    Total Value: ${getTotalValue().toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    Approval Level: {getApprovalLevel(getTotalValue()).level}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Step 3: Pricing & Terms Optimization */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Pricing & Terms Optimization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              AI-optimized pricing, volume discounts, and payment terms.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Volume Discounts
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography>100-499 units</Typography>
                        <Chip label="5% discount" color="success" size="small" />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography>500-999 units</Typography>
                        <Chip label="8% discount" color="success" size="small" />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography>1000+ units</Typography>
                        <Chip label="12% discount" color="success" size="small" />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Payment Terms
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Payment Terms</InputLabel>
                      <Select value="net30" label="Payment Terms">
                        <MenuItem value="net15">Net 15</MenuItem>
                        <MenuItem value="net30">Net 30</MenuItem>
                        <MenuItem value="net60">Net 60</MenuItem>
                        <MenuItem value="2/10net30">2/10 Net 30</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      Early payment discount: 2% if paid within 10 days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 4: Approval Workflow & Routing */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Approval Workflow & Routing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Dynamic approval routing based on order value and AI recommendations.
            </Typography>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Approval Matrix
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Value</TableCell>
                        <TableCell>Approvers</TableCell>
                        <TableCell>Estimated Time</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Under $1,000</TableCell>
                        <TableCell>
                          <Chip icon={<CheckCircle />} label="Auto-approve" color="success" size="small" />
                        </TableCell>
                        <TableCell>Immediate</TableCell>
                        <TableCell>
                          <Chip label="Ready" color="success" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>$1,000 - $10,000</TableCell>
                        <TableCell>
                          <Chip icon={<Business />} label="Manager" color="info" size="small" />
                        </TableCell>
                        <TableCell>2-4 hours</TableCell>
                        <TableCell>
                          <Chip label="Pending" color="warning" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>$10,000 - $50,000</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip icon={<Business />} label="Director" color="warning" size="small" />
                            <Chip icon={<AttachMoney />} label="Finance" color="warning" size="small" />
                          </Box>
                        </TableCell>
                        <TableCell>1-2 days</TableCell>
                        <TableCell>
                          <Chip label="Pending" color="warning" size="small" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Step 5: Review & Confirmation */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Confirmation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Final review of AI-optimized purchase orders before submission.
            </Typography>

            {purchaseOrders.map((po) => (
              <Card key={po.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Purchase Order {po.id}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={po.approvalLevel} 
                        color={po.approvalLevel === 'Auto-approve' ? 'success' : 'warning'}
                        size="small"
                      />
                      <Chip 
                        label={`$${po.totalValue.toLocaleString()}`} 
                        color="primary" 
                        size="small"
                      />
                    </Box>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {po.items.map((item) => (
                          <TableRow key={item.sku}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell align="right">${item.unitPrice.toFixed(2)}</TableCell>
                            <TableCell align="right">${item.totalPrice.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="success.dark">
                      <strong>AI Insights:</strong> Estimated savings of ${po.estimatedSavings.toLocaleString()} 
                      with {po.aiConfidence}% confidence level.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box>
          <Button onClick={handleReset} sx={{ mr: 1 }}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {activeStep === steps.length - 1 ? 'Create Purchase Orders' : 'Next'}
          </Button>
        </Box>
      </Box>

      {/* AI Analysis Dialog */}
      <Dialog open={showAiDialog} onClose={() => setShowAiDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy color="primary" />
            AI Analysis Complete
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Our AI has analyzed your purchase order and identified several optimization opportunities:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {aiSuggestions.map((suggestion, index) => (
              <Alert key={index} severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{suggestion.title}:</strong> {suggestion.description}
                </Typography>
              </Alert>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAiDialog(false)}>Close</Button>
          <Button variant="contained" onClick={() => setShowAiDialog(false)}>
            Apply Recommendations
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
